import { useRef, useState } from "react";
import { FileIcon, PaperclipIcon, XIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import {
  COMPOSER_ATTACHMENT_ACCEPT,
  formatAttachmentSize,
  reviewInlineFileAttachments,
} from "./attachmentFiles";

export function QuestionAttachmentPicker({
  files,
  otherFileCount,
  disabled,
  onChange,
}: {
  files: File[];
  otherFileCount: number;
  disabled: boolean;
  onChange: (files: File[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const add = (incoming: File[]) => {
    if (disabled) return;
    const { accepted, error } = reviewInlineFileAttachments({
      files: incoming,
      reservedCount: otherFileCount + files.length,
    });
    setError(error);
    if (accepted.length > 0) onChange([...files, ...accepted]);
  };
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      aria-label="Question attachments"
      onDragOver={(event) => {
        if (!disabled && event.dataTransfer.types.includes("Files")) event.preventDefault();
      }}
      onDrop={(event) => {
        if (disabled || !event.dataTransfer.files.length) return;
        event.preventDefault();
        event.stopPropagation();
        add(Array.from(event.dataTransfer.files));
      }}
    >
      {files.length > 0 ? (
        <ul className="contents" aria-label="Files for this answer">
          {files.map((file, index) => (
            <li
              key={`${file.name}:${index}`}
              className="inline-flex h-7 max-w-full min-w-0 items-center gap-1.5 rounded-md border border-border/70 bg-muted/40 ps-2 pe-0.5 text-xs sm:h-6"
            >
              <FileIcon className="size-3 shrink-0 text-muted-foreground" />
              <span className="max-w-48 truncate">{file.name}</span>
              <span className="shrink-0 text-muted-foreground tabular-nums">
                {formatAttachmentSize(file.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="text-muted-foreground"
                disabled={disabled}
                aria-label={`Remove ${file.name} from answer`}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        ref={input}
        type="file"
        multiple
        accept={COMPOSER_ATTACHMENT_ACCEPT}
        className="hidden"
        aria-label="Files for this answer"
        disabled={disabled}
        onChange={(event) => {
          add(Array.from(event.currentTarget.files ?? []));
          event.currentTarget.value = "";
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className={cn("text-muted-foreground hover:text-foreground", files.length === 0 && "-ms-2")}
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        <PaperclipIcon />
        Attach to answer
      </Button>
      {error ? (
        <p role="alert" className="basis-full text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
